import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Typography,
  Grid,
  Alert,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Collapse } from "@mui/material";
import Box from "@mui/material/Box";
import { collectLeadMetadata } from "../utils/leadAttribution";

// True only inside react-snap's headless Chrome during pre-rendering.
const isPrerendering =
  typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap");

function BookingForm() {
  const [hideForm, setHideForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const mountTime = useRef(Date.now());

  // Defer the interactive form to a client-side render after mount.
  // MUI's multiline TextField measures its size from the viewport (baking a
  // px width/height into the DOM via a layout effect). react-snap captures
  // those measured values into the pre-rendered HTML, where they never match a
  // visitor's device — causing a hydration mismatch (React #418) that forces
  // React to discard and re-render the whole page on every load (the flash).
  // Skipping the form during pre-render + initial hydration removes the only
  // mismatching markup, so the rest of the page hydrates cleanly.
  // The guard keeps `mounted` false during react-snap so the placeholder (not
  // the measured form) is what gets saved and what the client first hydrates.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (isPrerendering) return;
    setMounted(true);
  }, []);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    venueAddress: "",
    eventDate: null,
    numberOfDrummers: "",
    eventDetails: "",
    website: "", // Honeypot field
  });

  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, eventDate: date });
  };


  // GraphQL Mutation
  const SUBMIT_MUTATION = `
    mutation DirectLead($input: LeadInput!) {
      directLead(input: $input) {
        success
        message
      }
    }
  `;

  // Handles Form Submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    setStatusMessage({ type: "", text: "" });

    // Timing check - bots submit instantly, humans don't
    const elapsed = Date.now() - mountTime.current;
    if (elapsed < 3000) {
      setStatusMessage({ type: "success", text: "Booking submitted successfully!" });
      return;
    }

    // Honeypot validation - reject if filled (fake success to deceive bots)
    if (formData.website.trim() !== "") {
      setStatusMessage({ type: "success", text: "Booking submitted successfully!" });
      return;
    }

    // Full name length check
    if (formData.fullName.trim().length < 2 || formData.fullName.length > 100) {
      setStatusMessage({ type: "error", text: "Please enter a valid name." });
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setStatusMessage({ type: "error", text: "Please enter a valid email address." });
      return;
    }

    // Phone number validation - digits, spaces, dashes, plus, parens; 7-20 chars
    const phoneDigits = formData.phoneNumber.replace(/\D/g, "");
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      setStatusMessage({ type: "error", text: "Please enter a valid phone number." });
      return;
    }

    // Event date - must be today or in the future
    if (formData.eventDate && formData.eventDate.isBefore(new Date(), "day")) {
      setStatusMessage({ type: "error", text: "Event date must be in the future." });
      return;
    }

    // Event details length cap
    if (formData.eventDetails.length > 2000) {
      setStatusMessage({ type: "error", text: "Event details must be under 2000 characters." });
      return;
    }

    setSubmitting(true);

    // Where the visitor came from, what device they're on and their Clarity
    // session ids. Resolves to null (and is then omitted) if the browser
    // blocked storage or analytics — the enquiry goes through either way.
    const metadata = await collectLeadMetadata();

    const input = {
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      venueAddress: formData.venueAddress.trim(),
      eventDate: formData.eventDate
        ? formData.eventDate.format("YYYY-MM-DD")
        : null,
      numberOfDrummers: parseInt(formData.numberOfDrummers),
      eventDetails: formData.eventDetails.trim(),
    };

    if (metadata) {
      input.metadata = metadata;
    }

    const requestBody = {
      query: SUBMIT_MUTATION,
      variables: { input },
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_BACKEND_API_URL,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();
      if (result.data?.directLead?.success) {
        setHideForm(true);
        setStatusMessage({
          type: "success",
          text:
            result.data.directLead.message || "Booking submitted successfully!",
        });
        setFormData({
          fullName: "",
          email: "",
          phoneNumber: "",
          venueAddress: "",
          eventDate: null,
          numberOfDrummers: "",
          eventDetails: "",
          website: "",
        });
      } else {
        setStatusMessage({
          type: "error",
          text: result.errors?.[0]?.message || "Submission failed.",
        });
      }
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: "Network error. Please try again.",
      });
      console.error("GraphQL Error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Pre-render / pre-hydration placeholder. Keeps the heading and the
  // #booking-form scroll target, and reserves vertical space to avoid layout
  // shift when the real form swaps in just after mount.
  if (!mounted) {
    return (
      <Container maxWidth="sm" id="booking-form">
        <Typography variant="h4" gutterBottom textAlign="center">
          Enquire Now
        </Typography>
        <Box sx={{ minHeight: 560 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" id="booking-form">
      <Typography variant="h4" gutterBottom textAlign="center">
        Enquire Now
      </Typography>
      {statusMessage.text && (
        <Alert severity={statusMessage.type} sx={{ mb: 2 }}>
          {statusMessage.text}
        </Alert>
      )}

      <Box sx={{ minHeight: 0, overflow: "hidden" }}>
        <Collapse in={!hideForm}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Full Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  variant="outlined"
                  required
                  value={formData.fullName}
                  onChange={handleChange("fullName")}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  required
                  value={formData.email}
                  onChange={handleChange("email")}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  variant="outlined"
                  required
                  value={formData.phoneNumber}
                  onChange={handleChange("phoneNumber")}
                  placeholder="+44 7123 456789"
                />
              </Grid>

              {/* Venue Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location"
                  variant="outlined"
                  required
                  value={formData.venueAddress}
                  onChange={handleChange("venueAddress")}
                />
              </Grid>

              {/* Event Date */}
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Event Date"
                    value={formData.eventDate}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        variant: "outlined"
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Number of Drummers */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Drummers"
                  type="number"
                  variant="outlined"
                  required
                  value={formData.numberOfDrummers}
                  onChange={handleChange("numberOfDrummers")}
                  inputProps={{ min: 1, max: 20 }}
                />
              </Grid>

              {/* Event Details */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Event Details"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={formData.eventDetails}
                  onChange={handleChange("eventDetails")}
                  inputProps={{ maxLength: 2000 }}
                />
              </Grid>

              {/* Honeypot field - hidden from users */}
              <div style={{
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                pointerEvents: 'none',
                tabIndex: -1,
                height: 0,
                overflow: 'hidden'
              }}>
                <TextField
                  label="Website"
                  value={formData.website}
                  onChange={handleChange("website")}
                  autoComplete="off"
                />
              </div>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Typography gutterBottom textAlign="center">
                  By clicking submit, you agree to our <Link style={{ color: '#1A1A1A', textDecoration: 'none' }} to="/terms">Terms & Conditions</Link>.
                </Typography>
                <Button
                  type="submit"
                  variant="contained"
                  color="gold"
                  fullWidth
                  size="large"
                  disabled={submitting}
                  sx={{
                    px: 4,
                    py: 1.5,
                    boxShadow: '0 8px 20px rgba(255, 215, 0, 0.2)',
                  }}
                >
                  {submitting ? "Submitting..." : "Submit Booking"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Collapse>
      </Box>
    </Container>
  );
}

export default BookingForm;
